// reportGenerator.js - Utility for generating evaluation PDF reports
// This file requires jsPDF and jspdf-autotable libraries
// Install: npm install jspdf jspdf-autotable

import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Generates a comprehensive evaluation report based on the provided data
 * Mimics the format shown in the evaluation report image
 */
export const generateEvaluationReport = (evaluationData, ratingsData, competenciesData) => {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Helper function to add header
  const addHeader = () => {
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('EMPLOYEE COMPETENCY EVALUATION REPORT', pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Employee: ${evaluationData.employeeName}`, 15, 25);
    doc.text(`Job Title: ${evaluationData.jobTitle}`, 15, 30);
    doc.text(`Department: ${evaluationData.department}`, 15, 35);
    doc.text(`Evaluation Period: ${evaluationData.evaluationPeriod}`, pageWidth - 80, 25);
    doc.text(`Evaluated As: ${evaluationData.evaluateAs}`, pageWidth - 80, 30);
    doc.text(`Evaluation Type: ${evaluationData.evaluationType}`, pageWidth - 80, 35);
    
    doc.setLineWidth(0.5);
    doc.line(15, 40, pageWidth - 15, 40);
  };

  // Helper function to calculate performance metrics
  // groupRatings keys use the format: item_${item_id}_level_${level}
  const calculateMetrics = (groupRatings) => {
    let totalTasks = 0;
    let completedByLevel = { 1: 0, 2: 0, 3: 0, 4: 0 };
    let ratingsByLevel = { 1: [], 2: [], 3: [], 4: [] };

    Object.entries(groupRatings).forEach(([key, rating]) => {
      const levelMatch = key.match(/_level_(\d+)$/);
      if (levelMatch) {
        const level = parseInt(levelMatch[1], 10);
        if (level >= 1 && level <= 4) {
          totalTasks++;
          if (rating > 0) {
            completedByLevel[level]++;
            ratingsByLevel[level].push(rating);
          }
        }
      }
    });

    const competenceRecorded = {};
    [1, 2, 3, 4].forEach(level => {
      const tasksForLevel = Object.keys(groupRatings).filter(k => k.endsWith(`_level_${level}`)).length;
      competenceRecorded[level] = tasksForLevel > 0
        ? Math.round((completedByLevel[level] / tasksForLevel) * 100)
        : 0;
    });

    const allRatings = Object.values(ratingsByLevel).flat();
    const averageRating = allRatings.length > 0
      ? (allRatings.reduce((a, b) => a + b, 0) / allRatings.length).toFixed(1)
      : '0.0';

    return { totalTasks, completedByLevel, ratingsByLevel, competenceRecorded, averageRating };
  };

  // Helper function to get relevance letter based on criteria
  const getRelevance = (rating) => {
    // A: Not necessary, B: Nice to have, C: Expected, D: Necessary, E: Mission-critical
    // For this implementation, we'll map ratings to relevance
    if (rating === 5) return 'E'; // Regularly exceeds - Mission-critical
    if (rating === 4) return 'D'; // Satisfactory - Necessary
    if (rating === 3) return 'C'; // Average - Expected
    if (rating === 2) return 'B'; // Below expectation - Nice to have
    return 'A'; // Unsatisfactory - Not necessary
  };

  // Start building the report
  addHeader();
  
  let yPosition = 50;

  // competenciesData is an array: [{ group_id, group_name, competencies: [{ competency_id, competency_name, competency_items: [{ item_id, description }] }] }]
  // ratingsData keys use the format: item_${item_id}_level_${level}
  (competenciesData || []).forEach((group, groupIndex) => {
    if (groupIndex > 0 && yPosition > pageHeight - 60) {
      doc.addPage();
      yPosition = 20;
    }

    // Group Header
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(240, 240, 240);
    doc.rect(15, yPosition, pageWidth - 30, 8, 'F');
    doc.text(`${groupIndex + 1}. ${group.group_name}`, 17, yPosition + 5);
    yPosition += 12;

    // Build table data for this group
    const tableData = [];
    const subHeaders = [
      'Competency',
      'A', 'B', 'C', 'D', 'E', 'F',
      'A', 'B', 'C', 'D', 'E', 'F',
      'A', 'B', 'C', 'D', 'E', 'F',
      'A', 'B', 'C', 'D', 'E', 'F'
    ];

    (group.competencies || []).forEach((competency) => {
      (competency.competency_items || []).forEach((item) => {
        const row = [item.description];

        for (let level = 1; level <= 4; level++) {
          const key = `item_${item.item_id}_level_${level}`;
          const rating = ratingsData[key] || 0;
          const relevance = rating > 0 ? getRelevance(rating) : '';
          ['A', 'B', 'C', 'D', 'E', 'F'].forEach(rel => {
            row.push(relevance === rel ? '✓' : '');
          });
        }

        tableData.push(row);
      });
    });

    // Calculate metrics for this group
    const groupRatings = {};
    (group.competencies || []).forEach((competency) => {
      (competency.competency_items || []).forEach((item) => {
        for (let level = 1; level <= 4; level++) {
          const key = `item_${item.item_id}_level_${level}`;
          if (ratingsData[key]) {
            groupRatings[key] = ratingsData[key];
          }
        }
      });
    });
    
    const metrics = calculateMetrics(groupRatings);
    
    // Add metrics rows - Fixed to properly spread the values across columns
    const performedWellRow = ['(1) Performed well (>3)'];
    const multiplyRow = ['Multiply the two rows above (b)'];
    const competenceRecordedRow = ['Competence recorded in the period under review - (total b) %'];
    
    // Add values for each level (6 columns per level = 24 total)
    [1, 2, 3, 4].forEach(level => {
      const count = metrics.completedByLevel[level] || 0;
      const percentage = metrics.competenceRecorded[level] || 0;
      
      // Add the value in the first column of each level group, empty for others
      performedWellRow.push(count.toString());
      performedWellRow.push(...Array(5).fill(''));
      
      multiplyRow.push(count.toString());
      multiplyRow.push(...Array(5).fill(''));
      
      competenceRecordedRow.push(`${percentage}%`);
      competenceRecordedRow.push(...Array(5).fill(''));
    });
    
    const metricsRows = [
      ['Performance observed in the period under review - Total tasks', ...Array(24).fill('')],
      performedWellRow,
      multiplyRow,
      ['', ...Array(24).fill('')],
      competenceRecordedRow,
      ['', ...Array(24).fill('')],
      ['Average : 3 targeted competence of', metrics.averageRating, ...Array(23).fill('')]
    ];
    
    // Create the table
    doc.autoTable({
      startY: yPosition,
      head: [subHeaders],
      body: [...tableData, ...metricsRows],
      theme: 'grid',
      styles: {
        fontSize: 7,
        cellPadding: 1,
        lineColor: [200, 200, 200],
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 60, fontSize: 7 },
        ...Object.fromEntries(
          Array.from({ length: 24 }, (_, i) => [i + 1, { cellWidth: 8, halign: 'center' }])
        )
      },
      didDrawCell: (data) => {
        // Highlight cells with checkmarks
        if (data.cell.raw === '✓') {
          doc.setFillColor(255, 230, 200);
          doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
          doc.setTextColor(0, 0, 0);
          doc.text('✓', data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, {
            align: 'center',
            baseline: 'middle'
          });
        }
      }
    });
    
    yPosition = doc.lastAutoTable.finalY + 10;
  });
  
  // Add legend on last page
  if (yPosition > pageHeight - 40) {
    doc.addPage();
    yPosition = 20;
  }
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('EVALUATION KEY:', 15, yPosition);
  yPosition += 7;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const legend = [
    '1: Unsatisfactory | 2: Regularly below expectation | 3: Average | 4: Satisfactory | 5: Regularly exceeds expectation',
    '',
    'The relevance of each competence in the role is represented by a scale between A and F, denoting as follows:',
    'A: Not necessary | B: Nice to have | C: Expected | D: Necessary | E: Mission-critical | F: [Reserved for future use]'
  ];
  
  legend.forEach((line, idx) => {
    doc.text(line, 15, yPosition + (idx * 5));
  });
  
  // Add footer with date
  const today = new Date().toLocaleDateString();
  doc.setFontSize(8);
  doc.text(`Generated on: ${today}`, 15, pageHeight - 10);
  doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth - 30, pageHeight - 10);
  
  // Save the PDF
  const filename = `Evaluation_${evaluationData.employeeName.replace(/\s+/g, '_')}_${today.replace(/\//g, '-')}.pdf`;
  doc.save(filename);
};

export default generateEvaluationReport;